<template>
  <div
    v-if="rows"
    class="details-grid"
  >
    <div
      v-for="(row, i) in rows"
      :key="i"
      class="row"
    >
      <div class="label">
        {{ row.label }}:
        <Tooltip
          v-if="row.tooltip"
          position="left"
        >
          {{ row.tooltip }}
        </Tooltip>
      </div>

      <div
        class="value"
        :translate="row.translate === false ? 'no' : null"
      >
        <div
          v-if="row.format === 'string'"
          class="flex q-gap-sm items-center fit"
        >
          <q-icon
            v-if="row.icon"
            size="sm"
            :name="row.icon"
          />
          {{ row.value }}
        </div>
        <div v-else-if="row.format === 'array'">
          <div
            v-for="(v, vi) in row.value"
            :key="vi"
            class="flex"
          >
            {{ v }}
          </div>
        </div>
        <div v-else-if="row.format === 'object'">
          <div
            v-for="(v, vi) in row.value"
            :key="vi"
            class="flex q-gap-sm"
          >
            <div class="text-medium">
              {{ `${v[0]}:` }}
            </div>
            <div>
              {{ v[1] }}
            </div>
          </div>
        </div>
        <div v-else-if="row.format === 'badge'">
          <Badge
            :label="typeof row.value !== 'boolean' ? row.value : null"
            :color="typeof row.value === 'boolean'
              ? row.value
                ? 'positive'
                : 'negative'
              : row.color"
            :start-icon="typeof row.value === 'boolean'
              ? row.value
                ? 'mono-check'
                : 'mono-cross'
              : row.icon"
            :is-icon="row.icon || typeof row.value === 'boolean'"
          />
        </div>
        <div v-else-if="row.format === 'link' || row.format === 'url'">
          <a
            :href="row.value"
            target="_blank"
          >
            <span>{{ parseUrl(row.value) }}</span> <span><q-icon class="mono-external-link" /></span>
          </a>
        </div>
        <div v-else-if="row.format === 'country'">
          <div class="country">
            <q-icon
              class="flag"
              :name="row.flag"
            />
            <span class="name">{{ row.value }}</span>
          </div>
        </div>
        <div v-else-if="row.format === 'boolean'">
          <Badge
            :color="row.value ? 'positive' : 'negative'"
            :start-icon="row.value ? 'mono-check-circle-outline' : 'mono-cross-circle-outline'"
          />
        </div>
        <div v-else-if="row.format === 'null'">
          <Badge
            color="disabled"
            start-icon="mono-null"
          />
        </div>
        <div v-else-if="row.format === 'integer'">
          <Badge
            color="info"
            :label="row.value != null ? String(row.value) : '—'"
          />
        </div>
        <div v-else-if="row.format === 'timestamp'">
          {{ row.value != null ? formatDate(row.value * 1000, 'DD MMM YYYY') : '—' }}
        </div>
        <div v-else>
          {{ row.value ?? '—' }}
        </div>
        <!-- {{ console.log(row.label, row.format, row.value) }} -->
      </div>
    </div>
  </div>
</template>

<script setup>
import { date } from 'quasar'

defineOptions({ name: 'DetailsGrid' })

const props = defineProps({
  rows: { type: Array, default: null }
})

const { formatDate } = date

const parseUrl = url => {
  url = url.split('/')
  return url[url.length - 2]
}

</script>

<style scoped>
@reference "#app-styles.css";

.details-grid {
  @apply self-start grid grid-cols-[max-content_1fr] w-full;
  @apply text-[12px] leading-3.5 font-normal;

  .row {
    @apply grid grid-cols-subgrid col-span-full gap-x-md gap-y-sm;
    @apply items-start min-h-(--generic-row-height-dense) px-lg py-xs;
    @apply transition-all duration-300 ease-out;
    @apply hover:layer-highlight-faint hover:cursor-pointer;
  }

  .label,
  .value { @apply flex items-center; }

  .label { @apply ink-subtle whitespace-nowrap self-start; }

  .value {
    @apply ink-strong wrap-anywhere break-all hyphens-auto;

    .q-badge { @apply min-w-10 justify-center; }
    .country {
      @apply flex flex-nowrap items-center gap-sm;
      .flag { @apply text-[16px] leading-4; }
      .name {}
    }
  }
}

</style>
